// @ts-check

import { Component } from '@theme/component';
import { ThemeEvents, CustomizerToastEvent } from '@theme/events';
import { track } from '@theme/custom.customizer-analytics';

/** @typedef {import('@theme/custom.customizer-store').CustomizerStore} CustomizerStore */

/**
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} [bwButton] - The B&W toggle (reflects state via `aria-pressed`).
 *   Absent on a scan face, which ships as uploaded.
 * @property {HTMLElement} [bwNote] - Says why the toggle is locked, when the method locks it.
 */

/**
 * Edit Photo toolbar. The crop canvas lives in the hero preview and mirrors the store via
 * `customizer:update`. The Liquid `on:` bindings need the leading slash (`/change`) or they no-op.
 *
 * @extends Component<Refs>
 */
class CustomizerPhotoEdit extends Component {
  /** The photo last measured. Each upload is a fresh object URL, so a new one means a new photo. */
  #measuredUrl = '';

  /** The shorter side of the photo at {@link #measuredUrl} in pixels, null until it is known. */
  #measuredEdge = /** @type {number | null} */ (null);

  /** Whether the small photo warning was last raised, so it slides in once each time it applies. */
  #warned = false;

  /** The customizer the warning went to: once we're detached, there's no host to read it from. */
  #warnedConfigId = '';

  #warningFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.customizerUpdate, this.#syncBw);
    document.addEventListener(ThemeEvents.customizerUpdate, this.#syncPhotoSize);
    document.addEventListener(ThemeEvents.customizerPhotoEdit, this.#queueWarning);
    this.#syncBw();
    this.#syncPhotoSize();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#syncBw);
    document.removeEventListener(ThemeEvents.customizerUpdate, this.#syncPhotoSize);
    document.removeEventListener(ThemeEvents.customizerPhotoEdit, this.#queueWarning);
    cancelAnimationFrame(this.#warningFrame);
    // Gone with the warning up: take it down, or it stays its full time over whatever replaced us.
    if (this.#warned) {
      this.#warned = false;
      document.dispatchEvent(new CustomizerToastEvent(this.#warnedConfigId, this, null));
    }
  }

  /** @returns {CustomizerStore | undefined} */
  get #store() {
    const host = /** @type {{ store?: CustomizerStore } | null} */ (
      this.closest('custom-customizer')
    );
    return host?.store;
  }

  /** @returns {string} The bound upload control's key. */
  get #key() {
    return this.dataset.controlKey ?? '';
  }

  /** A replaced photo clears the stored edit itself, so this only re-opens the picker. */
  change() {
    const picker = this.closest('custom-customizer-engrave')?.querySelector(
      `custom-customizer-control[data-control-key="${this.#key}"] input[type="file"]`
    );
    if (picker instanceof HTMLInputElement) picker.click();
  }

  /** B&W is a free flag on the stored transform. Where the method is already black & white the
   * toggle is locked on, so the click does nothing rather than ordering a colour etch. */
  toggleBw() {
    const store = this.#store;
    if (!store || store.photoBwLocked(this.#key)) return;
    const current = store.photoTransform(this.#key);
    store.setControlTransform(this.#key, { ...current, bw: !current.bw });
  }

  /** A cleared transform is the untouched photo, so Reset stores null instead of the defaults. */
  reset() {
    this.#store?.setControlTransform(this.#key, null);
  }

  #syncBw = () => {
    const { bwButton, bwNote } = this.refs;
    if (!bwButton) return;
    const store = this.#store;
    const transform = store?.photoTransform(this.#key);
    const locked = Boolean(store?.photoBwLocked(this.#key));
    bwButton.setAttribute('aria-pressed', String(transform ? transform.bw : false));
    // aria-disabled, not `disabled`: a locked toggle is ON, and the dimmed look of a disabled
    // control would read as off. The note beside it says why it cannot move.
    bwButton.setAttribute('aria-disabled', String(locked));
    if (bwNote) bwNote.hidden = !locked;
  };

  /** Measure each new photo once, then judge it against the crop on every update, zoom included. */
  #syncPhotoSize = () => {
    if (!this.dataset.minPhotoEdge) return;
    const value = this.#store?.getControlValue(this.#key);
    const url = typeof value === 'string' ? value : '';
    if (url !== this.#measuredUrl) {
      this.#measuredUrl = url;
      this.#measuredEdge = null;
      if (url) void this.#measure(url);
    }
    this.#queueWarning();
  };

  /** @param {string} url - The photo's object URL. */
  async #measure(url) {
    const edge = await shortEdge(url);
    // A replaced or removed photo owns the warning now.
    if (url !== this.#measuredUrl) return;
    this.#measuredEdge = edge;
    this.#queueWarning();
  }

  /** Waits a frame, so a step or view switch that set this update off has shown or hidden us. */
  #queueWarning = () => {
    cancelAnimationFrame(this.#warningFrame);
    this.#warningFrame = requestAnimationFrame(() => this.#syncWarning());
  };

  /** Slides the warning in when a small photo comes on screen, and takes it down once fixed. */
  #syncWarning() {
    const warn = this.#isSmall() && this.getClientRects().length > 0;
    if (warn === this.#warned) return;
    this.#warned = warn;

    const host = /** @type {HTMLElement | null} */ (this.closest('custom-customizer'));
    if (warn) {
      this.#warnedConfigId = host?.dataset.configId ?? '';
      track(this, 'photo_low_res');
    }
    const message = warn
      ? { title: this.dataset.smallPhotoTitle ?? '', body: this.dataset.smallPhotoBody ?? '' }
      : null;
    document.dispatchEvent(new CustomizerToastEvent(this.#warnedConfigId, this, message));
  }

  /** @returns {boolean} */
  #isSmall() {
    const minEdge = Number(this.dataset.minPhotoEdge);
    const edge = this.#croppedEdge();
    return edge !== null && minEdge > 0 && edge < minEdge;
  }

  /**
   * The measured short edge over the crop's zoom: a photo zoomed 3x onto a face prints a third of
   * its pixels across the frame. `scale` is relative to cover-fit, so 1 is the untouched photo.
   * @returns {number | null}
   */
  #croppedEdge() {
    if (this.#measuredEdge === null) return null;
    const scale = this.#store?.photoTransform(this.#key)?.scale ?? 1;
    return this.#measuredEdge / (scale > 0 ? scale : 1);
  }
}

/**
 * The shorter side of an image, in pixels. Ingest has already refused a file that will not decode,
 * so a failed load here measures nothing and no warning shows.
 * @param {string} url
 * @returns {Promise<number | null>}
 */
function shortEdge(url) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(Math.min(image.naturalWidth, image.naturalHeight) || null);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

if (!customElements.get('custom-customizer-photo-edit')) {
  customElements.define('custom-customizer-photo-edit', CustomizerPhotoEdit);
}
